import { Link } from "react-router-dom";
import { Check, X, AlertTriangle, QrCode, ClipboardCheck, ChevronRight } from "lucide-react";
import { Logo } from "@/components/Logo";

const GOLD = "#C9A84C";
const NAVY = "#0A1628";
const CARD = "#112240";
const MUTED = "#8892B0";
const GREEN = "#00C896";
const RED = "#EF4444";

/* --- Hero mockup: the O&M command centre, built from real UI patterns with sample data --- */
export const DashboardMockup = () => (
  <div className="rounded-xl border overflow-hidden shadow-2xl shadow-black/60"
       style={{ background: NAVY, borderColor: "rgba(201,168,76,0.25)" }} data-testid="om-landing-dashboard-mockup">
    <div className="flex items-center gap-1.5 px-3 py-2 border-b" style={{ background: CARD, borderColor: "rgba(255,255,255,0.08)" }}>
      <span className="w-2 h-2 rounded-full" style={{ background: "rgba(239,68,68,0.7)" }} />
      <span className="w-2 h-2 rounded-full" style={{ background: "rgba(245,158,11,0.7)" }} />
      <span className="w-2 h-2 rounded-full" style={{ background: "rgba(16,185,129,0.7)" }} />
      <span className="num text-[10px] ml-2" style={{ color: MUTED }}>plansarthi.com — O&amp;M Command Centre</span>
    </div>
    <div className="p-4">
      <div className="grid grid-cols-4 gap-2">
        {[["PM compliance", "93%"], ["Open tickets", "4"], ["Avg health", "87"], ["SLA met", "96%"]].map(([l, v]) => (
          <div key={l} className="rounded-lg border p-2.5" style={{ background: CARD, borderColor: "rgba(255,255,255,0.08)" }}>
            <div className="text-[8px] uppercase tracking-[0.12em]" style={{ color: MUTED }}>{l}</div>
            <div className="font-semibold num text-[17px] mt-0.5" style={{ color: GOLD }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border p-3 mt-2.5" style={{ background: CARD, borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-white font-medium">Reliability trend — MTBF vs MTTR</span>
          <span className="text-[8.5px]" style={{ color: GOLD }}>Last 90 days</span>
        </div>
        <svg viewBox="0 0 300 76" className="w-full mt-2" style={{ height: 76 }} aria-hidden="true">
          {[0, 19, 38, 57].map((y) => <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="rgba(255,255,255,0.06)" />)}
          <polyline fill="none" stroke={GOLD} strokeWidth="2"
                    points="0,58 33,54 66,49 99,52 132,42 165,36 198,30 231,26 264,20 300,14" />
          <polyline fill="none" stroke={RED} strokeWidth="2"
                    points="0,22 33,26 66,24 99,31 132,34 165,38 198,42 231,45 264,50 300,54" />
        </svg>
        <div className="flex gap-4 text-[8.5px]" style={{ color: MUTED }}>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-px inline-block" style={{ background: GOLD }} /> MTBF rising</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-px inline-block" style={{ background: RED }} /> MTTR falling</span>
        </div>
      </div>

      <div className="rounded-lg border p-3 mt-2.5" style={{ background: CARD, borderColor: `${RED}55` }}>
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" style={{ color: RED }} />
          <span className="text-[10px] font-semibold text-white">CM-0042 · Critical · Power Transformer TR-01</span>
          <span className="ml-auto text-[8.5px] px-1.5 py-0.5 rounded-full" style={{ background: `${RED}22`, color: RED }}>SLA 2h left</span>
        </div>
        <p className="text-[9px] mt-1.5" style={{ color: MUTED }}>
          Winding temperature above rated threshold — RCA drafted automatically from the fault taxonomy.
        </p>
        <div className="flex gap-1.5 mt-2">
          {["5-Why ready", "Oil sample due", "Assigned: R. Menon"].map((t) => (
            <span key={t} className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: NAVY, color: GOLD }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* --- Phone mockup: the QR scan landing page a technician sees --- */
export const PhoneMockup = () => (
  <div className="mx-auto rounded-[2.2rem] border-[7px] p-3 shadow-2xl shadow-black/70"
       style={{ width: 268, background: NAVY, borderColor: "#1B2A44" }} data-testid="om-landing-phone-mockup">
    <div className="h-1 w-14 rounded-full mx-auto mb-3" style={{ background: "#2A3B57" }} />
    <div className="rounded-lg border p-3" style={{ background: CARD, borderColor: "rgba(201,168,76,0.2)" }}>
      <div className="flex items-center gap-2">
        <QrCode className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
        <div className="min-w-0">
          <div className="text-[8.5px]" style={{ color: MUTED }}>AST-0009</div>
          <div className="text-[11.5px] font-semibold text-white truncate">Power Transformer TR-01</div>
        </div>
      </div>
      <div className="flex gap-1.5 mt-2">
        {["Operational", "Electrical"].map((t) => (
          <span key={t} className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: NAVY, color: GOLD }}>{t}</span>
        ))}
      </div>
    </div>

    <div className="rounded-lg border p-2.5 mt-2.5" style={{ background: `${RED}18`, borderColor: `${RED}55` }}>
      <div className="flex items-start gap-1.5">
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" style={{ color: RED }} />
        <p className="text-[9px] leading-snug" style={{ color: "#FCA5A5" }}>
          Winding temp 96°C — above the expected 45–85°C band. Manager alerted on WhatsApp.
        </p>
      </div>
    </div>

    <button className="w-full mt-2.5 h-10 rounded-lg text-[12px] font-semibold inline-flex items-center justify-center gap-1.5"
            style={{ background: GOLD, color: NAVY }} tabIndex={-1}>
      <ClipboardCheck className="w-3.5 h-3.5" /> Log a Daily Reading
    </button>
    <button className="w-full mt-2 h-10 rounded-lg text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 border"
            style={{ borderColor: GOLD, color: GOLD }} tabIndex={-1}>
      <AlertTriangle className="w-3.5 h-3.5" /> Raise a Fault Ticket
    </button>
    <p className="text-[8.5px] text-center mt-2.5" style={{ color: MUTED }}>Scanned from the asset label · no app installed</p>
  </div>
);

/* --- Comparison table: a real table on desktop, stacked cards on mobile --- */
const ROWS = [
  ["Asset Register", "Centralised, searchable, QR-linked", "Excel spreadsheet"],
  ["PM Scheduling", "Auto-scheduled work orders with checklists", "Calendar reminders or verbal instructions"],
  ["Fault Reporting", "Structured ticket with SLA timer", "WhatsApp message or phone call"],
  ["Compliance Reporting", "One-click branded PDF and Excel", "Manual data collection, 2 to 3 days"],
  ["Root Cause Analysis", "Automated 5-Why RCA with PDF export", "Word document or not done at all"],
  ["Reliability Metrics", "Live MTBF and MTTR with trend analysis", "Not calculated or calculated quarterly in Excel"],
  ["Field Access", "Mobile-first, QR scan, works on any phone", "Laptop or printed checklist"],
  ["Alerts", "WhatsApp and email, instant", "Phone call or delayed report"],
  ["Audit Readiness", "All records digital, searchable, signed off", "Paper files or shared folder"],
];

export const ComparisonTable = () => (
  <>
    <div className="hidden md:block overflow-hidden rounded-lg border" style={{ borderColor: "rgba(201,168,76,0.25)" }}>
      <table className="w-full text-left" data-testid="om-landing-comparison-table">
        <thead>
          <tr style={{ background: CARD }}>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] w-[26%]" style={{ color: MUTED }}>Feature</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: GOLD }}>Plan-Sarthi</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: MUTED }}>Traditional / Manual</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([feature, ours, theirs], i) => (
            <tr key={feature} style={{ background: i % 2 ? CARD : NAVY }} data-testid={`comparison-row-${i}`}>
              <td className="px-4 py-3 text-[13px] font-medium text-white align-top">{feature}</td>
              <td className="px-4 py-3 text-[13px] text-white align-top">
                <span className="flex items-start gap-2">
                  <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GREEN }} /> {ours}
                </span>
              </td>
              <td className="px-4 py-3 text-[13px] align-top" style={{ color: MUTED }}>
                <span className="flex items-start gap-2">
                  <X className="w-4 h-4 shrink-0 mt-0.5" style={{ color: RED }} /> {theirs}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="md:hidden space-y-2.5" data-testid="om-landing-comparison-cards">
      {ROWS.map(([feature, ours, theirs], i) => (
        <div key={feature} className="rounded-lg border p-4" style={{ background: CARD, borderColor: "rgba(255,255,255,0.1)" }}
             data-testid={`comparison-card-${i}`}>
          <h3 className="text-[14px] font-semibold text-white">{feature}</h3>
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3 mt-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] font-semibold" style={{ color: GOLD }}>Plan-Sarthi</div>
              <p className="text-[12px] text-white mt-1.5 flex items-start gap-1.5">
                <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: GREEN }} /> {ours}
              </p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] font-semibold" style={{ color: MUTED }}>Traditional</div>
              <p className="text-[12px] mt-1.5 flex items-start gap-1.5" style={{ color: MUTED }}>
                <X className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: RED }} /> {theirs}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </>
);

/* --- Sticky in-page section nav --- */
export const OmLandingNav = ({ links }) => (
  <div className="sticky top-0 z-40 border-b backdrop-blur"
       style={{ background: "rgba(10,22,40,0.92)", borderColor: "rgba(201,168,76,0.22)" }} data-testid="om-landing-nav">
    <div className="max-w-[1280px] mx-auto px-5 sm:px-8 h-12 flex items-center gap-5">
      <Link to="/" className="shrink-0" data-testid="om-landing-nav-logo">
        <Logo mono size="sm" className="text-white" />
      </Link>
      <div className="flex-1 min-w-0 flex items-center justify-center gap-5 overflow-x-auto sector-scroll">
        {links.map(([label, id]) => (
          <a key={label} href={`#${id}`}
             className="shrink-0 text-[12.5px] transition-colors duration-150 hover:!text-[#C9A84C]"
             style={{ color: MUTED }} data-testid={`om-landing-anchor-${id}`}>
            {label}
          </a>
        ))}
      </div>
      <Link to="/signup" className="shrink-0 inline-flex items-center h-8 px-3.5 rounded-lg text-[12.5px] font-semibold"
            style={{ background: GOLD, color: NAVY }} data-testid="om-landing-nav-trial-btn">
        Start Free Trial
      </Link>
    </div>
  </div>
);

export const Breadcrumb = () => (
  <nav className="max-w-[1280px] mx-auto px-5 sm:px-8 py-3 flex items-center gap-1.5 text-[12px]"
       style={{ color: MUTED }} aria-label="Breadcrumb" data-testid="om-landing-breadcrumb">
    <Link to="/" className="hover:!text-[#C9A84C] transition-colors duration-150">Home</Link>
    <ChevronRight className="w-3.5 h-3.5" />
    <span className="text-white">O&amp;M</span>
  </nav>
);
